if (!customElements.get('our-customers-carousel')) {
  class OurCustomersCarousel extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.our-customers__viewport');
      this.track = this.querySelector('.our-customers__track');
      this.items = Array.from(this.querySelectorAll('.our-customers__item'));
      this.prevBtn = this.querySelector('.our-customers__arrow--prev');
      this.nextBtn = this.querySelector('.our-customers__arrow--next');

      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragDeltaX = 0;
      this.startTransformX = 0;
      this.autoplayTimer = null;

      this.enableLoop = this.dataset.enableLoop === 'true';
      this.enableAutoplay = this.dataset.enableAutoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 4) * 1000;

      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.nextSlide = this.nextSlide.bind(this);
      this.prevSlide = this.prevSlide.bind(this);
    }

    connectedCallback() {
      if (!this.viewport || !this.track || this.items.length === 0) return;

      this.initEvents();
      this.updateCarouselState();

      if (this.enableAutoplay) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.removeEvents();
      this.stopAutoplay();
    }

    getVisibleItemsCount() {
      const width = window.innerWidth;
      const computedStyle = getComputedStyle(this.closest('.our-customers') || document.body);
      if (width >= 990) {
        return parseFloat(computedStyle.getPropertyValue('--desktop-cols')) || 3;
      } else if (width >= 750) {
        return parseFloat(computedStyle.getPropertyValue('--tablet-cols')) || 2;
      } else {
        return parseFloat(computedStyle.getPropertyValue('--mobile-cols')) || 1.2;
      }
    }

    getMaxIndex() {
      const visible = this.getVisibleItemsCount();
      return Math.max(0, Math.ceil(this.items.length - visible));
    }

    getItemStepWidth() {
      if (this.items.length === 0) return 0;
      const itemRect = this.items[0].getBoundingClientRect();
      const computedStyle = getComputedStyle(this.track);
      const gap = parseFloat(computedStyle.gap) || parseFloat(computedStyle.gridGap) || 20;
      return itemRect.width + gap;
    }

    updateCarouselState(animate = true) {
      const stepWidth = this.getItemStepWidth();
      const translateX = -1 * this.currentIndex * stepWidth;

      if (!animate) {
        this.track.classList.add('no-transition');
      } else {
        this.track.classList.remove('no-transition');
      }

      this.track.style.transform = `translate3d(${translateX}px, 0, 0)`;

      if (!animate) {
        // Force reflow
        this.track.offsetHeight;
        this.track.classList.remove('no-transition');
      }

      this.updateArrowStates();
    }

    updateArrowStates() {
      const maxIndex = this.getMaxIndex();
      if (this.prevBtn) {
        this.prevBtn.disabled = !this.enableLoop && this.currentIndex <= 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = !this.enableLoop && this.currentIndex >= maxIndex;
      }
    }

    nextSlide() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
      } else if (this.enableLoop) {
        this.currentIndex = 0;
      }
      this.updateCarouselState();
    }

    prevSlide() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else if (this.enableLoop) {
        this.currentIndex = maxIndex;
      }
      this.updateCarouselState();
    }

    goToIndex(index) {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      this.updateCarouselState();
    }

    initEvents() {
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.prevSlide);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.nextSlide);

      // Pointer / Touch drag
      this.viewport.addEventListener('mousedown', this.onPointerDown);
      this.viewport.addEventListener('touchstart', this.onPointerDown, { passive: true });

      window.addEventListener('mousemove', this.onPointerMove);
      window.addEventListener('touchmove', this.onPointerMove, { passive: false });

      window.addEventListener('mouseup', this.onPointerUp);
      window.addEventListener('touchend', this.onPointerUp);

      // Keyboard
      this.viewport.addEventListener('keydown', this.onKeyDown);

      // Autoplay pause triggers
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.enableAutoplay) this.startAutoplay();
      });
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => {
        if (this.enableAutoplay) this.startAutoplay();
      });

      // Shopify Editor
      document.addEventListener('shopify:block:select', (e) => {
        const blockId = e.detail.blockId;
        const blockIndex = this.items.findIndex(item => item.dataset.blockId === blockId);
        if (blockIndex !== -1) {
          this.goToIndex(blockIndex);
        }
      });
    }

    removeEvents() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('mousemove', this.onPointerMove);
      window.removeEventListener('touchmove', this.onPointerMove);
      window.removeEventListener('mouseup', this.onPointerUp);
      window.removeEventListener('touchend', this.onPointerUp);
    }

    onResize() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
      this.updateCarouselState(false);
    }

    onPointerDown(e) {
      this.isDragging = true;
      this.startX = e.touches ? e.touches[0].clientX : e.clientX;
      this.dragDeltaX = 0;

      const stepWidth = this.getItemStepWidth();
      this.startTransformX = -1 * this.currentIndex * stepWidth;

      this.track.classList.add('no-transition');
      this.viewport.classList.add('is-grabbing');

      this.stopAutoplay();
    }

    onPointerMove(e) {
      if (!this.isDragging) return;

      this.currentX = e.touches ? e.touches[0].clientX : e.clientX;
      this.dragDeltaX = this.currentX - this.startX;

      // Prevent scrolling page vertically if horizontal drag is dominant
      if (e.cancelable && Math.abs(this.dragDeltaX) > 10) {
        e.preventDefault();
      }

      const currentTransform = this.startTransformX + this.dragDeltaX;
      this.track.style.transform = `translate3d(${currentTransform}px, 0, 0)`;
    }

    onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;

      this.track.classList.remove('no-transition');
      this.viewport.classList.remove('is-grabbing');

      const stepWidth = this.getItemStepWidth();
      const threshold = stepWidth * 0.2 || 40;

      if (this.dragDeltaX < -threshold) {
        this.nextSlide();
      } else if (this.dragDeltaX > threshold) {
        this.prevSlide();
      } else {
        this.updateCarouselState();
      }

      if (this.enableAutoplay) {
        this.startAutoplay();
      }
    }

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    }

    startAutoplay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('our-customers-carousel', OurCustomersCarousel);
}
